/**
 * PortalManager: teleport portals between scenes.
 *
 * Each portal is a box trigger volume with a glowing translucent marker in the
 * world. When the player enters a portal, PORTAL_ENTER is emitted with the
 * target scene id; the destination scene manifest owns the arrival spawn.
 * Triggers once on entry; PORTAL_EXIT fires on leave.
 */

import { Entity, StandardMaterial, Vec3 } from 'playcanvas';
import type { Application } from 'playcanvas';
import { BLEND_NORMAL } from 'playcanvas';
import { eventBus } from '../events/EventBus';
import type { PortalConfig } from '../config/manifest';
import { worldIds } from '../render/LayerManager';

export class PortalManager {
    private _app: Application;
    private _portals: PortalConfig[];
    private _inside = new Set<string>();
    private _markers: Entity[] = [];

    constructor(app: Application, portals: PortalConfig[]) {
        this._app = app;
        this._portals = portals;
        this._buildMarkers();
    }

    private _buildMarkers() {
        for (const p of this._portals) this._buildMarker(p);
    }

    private _buildMarker(p: PortalConfig) {
        const mat = new StandardMaterial();
        mat.emissive.set(p.color?.[0] ?? 0.7, p.color?.[1] ?? 0.3, p.color?.[2] ?? 1.0);
        mat.emissiveIntensity = 1.4;
        mat.diffuse.set(0, 0, 0);
        mat.useLighting = false;
        mat.blendType = BLEND_NORMAL;
        mat.opacity = 0.35;
        mat.depthWrite = false;
        mat.depthTest = true;
        mat.update();

        const entity = new Entity(`portal-${p.id}`);
        entity.addComponent('render', { type: 'box' });
        entity.render!.material = mat;
        entity.render!.layers = worldIds();
        entity.setLocalScale(p.size[0], p.size[1], p.size[2]);
        entity.setPosition(p.position[0], p.position[1], p.position[2]);
        this._app.root.addChild(entity);
        this._markers.push(entity);
    }

    /** Add a portal at runtime (used by the trigger-point editor). */
    addPortal(p: PortalConfig) {
        this._portals.push(p);
        this._buildMarker(p);
    }

    /** Per-frame box-containment check. `playerPosition` is world space. */
    update(playerPosition: Vec3) {
        for (const p of this._portals) {
            const inside = this._isInsideBox(playerPosition, p.position, p.size);
            const wasInside = this._inside.has(p.id);
            if (inside && !wasInside) {
                this._inside.add(p.id);
                eventBus.emit({
                    type: 'PORTAL_ENTER',
                    id: p.id,
                    name: p.name,
                    targetRoomId: p.targetRoomId, targetScene: p.targetScene
                });
            } else if (!inside && wasInside) {
                this._inside.delete(p.id);
                eventBus.emit({ type: 'PORTAL_EXIT', id: p.id, name: p.name });
            }
        }
    }

    private _isInsideBox(p: Vec3, center: number[], size: number[]): boolean {
        return (
            Math.abs(p.x - center[0]) <= size[0] / 2 &&
            Math.abs(p.y - center[1]) <= size[1] / 2 &&
            Math.abs(p.z - center[2]) <= size[2] / 2
        );
    }

    /** Remove all portal markers (on scene unload). */
    destroy() {
        for (const m of this._markers) {
            this._app.root.removeChild(m);
            m.destroy();
        }
        this._markers.length = 0;
        this._inside.clear();
    }
}
